import { Application } from '@platform/client';
import { IndividualApplicationParameters } from '@platform/client/schemas';
import { ErrorBox, FormsInputDropdown } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Aanu-Gopald.svg';

type FormValues = {
  referralMethod: string;
};

const INDIVIDUAL_REFERRAL_OPTIONS = [
  { text: 'Search engine', textValue: 'search_engine' },
  { text: 'Social media', textValue: 'social_media' },
  { text: 'Referral from a colleague or friend', textValue: 'referral' },
  { text: 'From a partner or organization', textValue: 'partner' },
  { text: 'Email newsletter', textValue: 'email_newsletter' },
  { text: 'Other', textValue: 'other' },
];

export const IndividualSubmission = (props: ApplicationPageProps) => {
  const { onBack, progress, dataKey } = { ...props };
  const [failed, setFailed] = useState(false);

  const { getApplicationData } =
    useApplicationLocalStorage<IndividualApplicationParameters>(dataKey);

  const {
    handleSubmit,
    control,
    formState,
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onChange' });
  const { errors, isSubmitting, isSubmitSuccessful } = formState;

  const submitIndividualMutation = Application.useSubmitIndividualApplication();
  const queryClient = useQueryClient();

  const submit: SubmitHandler<FormValues> = (data) => {
    submitIndividualMutation.mutate(
      {
        data: {
          ...getApplicationData(),
          referralMethod: data.referralMethod,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: Application.getGetProfessionalApplicationStatusQueryKey(),
          });
        },
        onError: () => {
          setFailed(true);
        },
      },
    );
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      primaryCtaText={isSubmitSuccessful || isSubmitting ? 'Submitting' : 'Submit'}
      ctaDisabled={isSubmitSuccessful || isSubmitting}

    >
      <ApplicationPageContent
        title='Ready to submit? Just one more thing.'
        description='After you submit, your responses are final, so take a moment to make sure everything is just right.'
      >
        <FormsInputDropdown
          control={control}
          name='referralMethod'
          label='How did you find out about this opportunity?'
          placeholder='Select one'
          items={INDIVIDUAL_REFERRAL_OPTIONS}
          type='default'
          error={errors.referralMethod}
          inputDisabled={true}
        />
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
